import { Action, ButtonGroup, Page } from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { useState } from 'react';
import { match } from 'ts-pattern';
import {
  useStoreClaimTagDelete,
  useStoreClaimTags,
  useStoreClaimTagsCreate,
} from '~/hooks/useClaimTags';
import {
  useStoreRegistrationTagDelete,
  useStoreRegistrationTags,
  useStoreRegistrationTagsCreate,
} from '~/hooks/useRegistrationTags';
import {
  useStoreShippingClaimTagDelete,
  useStoreShippingClaimTags,
  useStoreShippingClaimTagsCreate,
} from '~/hooks/useShippingClaimTags';
import { TagsSettingsCard } from './TagsSettingsCard';

const tagEntities = ['Claim', 'Registration', 'Shipping Claim'] as const;

type TagEntity = (typeof tagEntities)[number];

export default function TagSettingsPage() {
  const [showAs, setShowAs] = useState<TagEntity>('Claim');
  const [showForm, setShowForm] = useState(false);

  const labels = {
    entitySingular: showAs,
    entityPlural: `${showAs}s`,
  };

  return (
    <Page
      title={`${showAs} Tags`}
      primaryAction={{
        onAction: () => setShowForm(true),
        icon: 'add',
        content: `Add ${showAs} Tag`,
      }}
      overrideActions={
        <ButtonGroup>
          {tagEntities.map((entity) => (
            <Action
              key={entity}
              onClick={() => setShowAs(entity)}
              pressed={showAs === entity}
              accessibilityLabel={`Show ${entity} Tags`}
            >
              {entity}
            </Action>
          ))}
        </ButtonGroup>
      }
    >
      {match(showAs)
        .with('Claim', () => (
          <TagsSettingsCard
            showForm={showForm}
            setShowForm={setShowForm}
            labels={labels}
            useTags={useStoreClaimTags}
            useTagCreate={useStoreClaimTagsCreate}
            useTagDelete={useStoreClaimTagDelete}
            createSchema={CrewMerchantUi.claimTagCreateSchema}
          />
        ))
        .with('Registration', () => (
          <TagsSettingsCard
            showForm={showForm}
            setShowForm={setShowForm}
            labels={labels}
            useTags={useStoreRegistrationTags}
            useTagCreate={useStoreRegistrationTagsCreate}
            useTagDelete={useStoreRegistrationTagDelete}
            createSchema={CrewMerchantUi.registrationTagCreateSchema}
          />
        ))
        .with('Shipping Claim', () => (
          <TagsSettingsCard
            showForm={showForm}
            setShowForm={setShowForm}
            labels={labels}
            useTags={useStoreShippingClaimTags}
            useTagCreate={useStoreShippingClaimTagsCreate}
            useTagDelete={useStoreShippingClaimTagDelete}
            createSchema={CrewMerchantUi.shippingClaimTagCreateSchema}
          />
        ))
        .exhaustive()}
    </Page>
  );
}
